import { useFragment } from '@apollo/client';
import { t } from 'ttag';

import { UNKNOWN_ERROR_MESSAGE } from '@constants';
import { useUserSubscription } from '@hook/useUserSubscription';
import { useIsAuth } from '@hook/useIsAuth';
import { notifyError, notifySuccess } from '@utils/notifications';
import { sendSellerSubscribeEvent } from '@utils/analytics';
import {
  ProductDetailsSellerInfoSkeleton,
  ProductDetailsSellerInfo,
} from '@components/product/ProductDetailsSellerInfo';

import { ProductDetailsSelelrInfoFragment, ProductDetailsSelelrInfoFragmentDoc } from './gql/graphql.v3.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsSellerInfoContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const isAuth = useIsAuth();

  const { data, complete } = useFragment<ProductDetailsSelelrInfoFragment>({
    fragment: ProductDetailsSelelrInfoFragmentDoc,
    from: product || null,
  });

  const {
    loading: isSubscriptionLoading,
    handleSubscribe,
    handleUnsubscribe,
  } = useUserSubscription();
  
  const handleToggleSubscription = () => {
    if (data?.seller?.id) {
      if (data.seller.isSubscribed) {
        handleUnsubscribe({
          userId: data.seller.id,
          onSuccess: () => notifySuccess(t`Ви успішно відписалися`),
          onError: () => notifyError(UNKNOWN_ERROR_MESSAGE()),
        });
      } else {
        handleSubscribe({
          userId: data.seller.id,
          onSuccess: () => {
            sendSellerSubscribeEvent({ referrer: 'user_profile' });
            notifySuccess(t`Ви успішно підписалися`);
          },
          onError: () => notifyError(UNKNOWN_ERROR_MESSAGE()),
        });
      }
    }
  };

  if (!complete) {
    return (
      <ProductDetailsSellerInfoSkeleton className={className} />
    );
  }

  if (data?.seller) {
    return (
      <ProductDetailsSellerInfo
        className={className}
        seller={data.seller}
        isAuth={!!isAuth}
        isSubscribed={data.seller.isSubscribed}
        isSubscriptionLoading={isSubscriptionLoading}
        onToggleSubscription={handleToggleSubscription}
      />
    );
  }

  return null;
};
